import React, { useCallback, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Icon,
  Link,
  Modal,
  StatusIndicator,
  Table,
  Textarea,
} from "@amzn/awsui-components-react";
import { useAppSelector } from "app/store";
import MarkdownRenderer from "./MarkdownRenderer";
import MessageCopyButton from "./MessageCopyButton";
import SessionFeedback from "./SessionFeedback";
import ToolCallIndicator from "./ToolCallIndicator";
import styles from "./style.module.scss";
import utilStyles from "styles/utils.module.scss";
import { useAssistantChat } from "./useAssistantChat";

export interface AssistantChatContentProps {
  onHeaderActions?: (actions: React.ReactNode) => void;
  alwaysActive?: boolean;
}

const AssistantChatContent: React.FC<AssistantChatContentProps> = ({
  onHeaderActions,
  alwaysActive = false,
}) => {
  const isOpen = useAppSelector((s) => s.assistant.isOpen);
  const userAlias = useAppSelector((state) => state.auth.user.alias);
  const headerSelectedTeam = useAppSelector(
    (state) => state.audits.headerSelectedTeam,
  );

  const {
    items,
    isConnected,
    isLoading,
    isLoadingHistory,
    error,
    inlineError,
    pendingConfirmation,
    sessionFeedback,
    sessions,
    selectedSessionId,
    sendMessage,
    sendFeedback,
    interruptResponse,
    handleConfirmation,
    handleSessionChange,
    refreshSessions,
    retry,
    dismissError,
  } = useAssistantChat({ alwaysActive });

  const [inputValue, setInputValue] = useState("");
  const [isSessionModalVisible, setIsSessionModalVisible] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const userInitials = userAlias
    ? userAlias.substring(0, 2).toUpperCase()
    : "U";

  const handleNewChat = useCallback(() => {
    handleSessionChange(null);
  }, [handleSessionChange]);

  React.useEffect(() => {
    onHeaderActions?.(
      <Button
        variant="icon"
        iconName="history"
        onClick={() => {
          setIsSessionModalVisible(true);
          refreshSessions();
        }}
        ariaLabel="Resume session"
      />,
    );
  }, [onHeaderActions, refreshSessions]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items]);

  const handleSend = useCallback(() => {
    const sent = sendMessage(inputValue);
    if (sent) setInputValue("");
  }, [inputValue, sendMessage]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSend();
    },
    [handleSend],
  );

  return (
    <>
      <Modal
        visible={isSessionModalVisible}
        onDismiss={() => setIsSessionModalVisible(false)}
        header="Resume a session"
        size="large"
      >
        <div className={utilStyles.scrollContainer60vh}>
          <Table
            items={sessions}
            columnDefinitions={[
              {
                id: "title",
                header: "Title",
                cell: (item) => item.title || item.sessionId,
              },
              {
                id: "updatedAt",
                header: "Last active",
                cell: (item) =>
                  new Date(item.updatedAt * 1000).toLocaleString(),
              },
              {
                id: "action",
                header: "",
                cell: (item) => (
                  <Button
                    variant="link"
                    onClick={() => {
                      handleSessionChange(item.sessionId);
                      setIsSessionModalVisible(false);
                    }}
                  >
                    Resume
                  </Button>
                ),
              },
            ]}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No sessions</b>
                <Box variant="p" color="inherit">
                  No previous sessions found.
                </Box>
              </Box>
            }
          />
        </div>
      </Modal>

      <div className={styles.messagesContainer}>
        {!isConnected && (isOpen || alwaysActive) && (
          <div className={styles.emptyState}>
            {error ? (
              <>
                <StatusIndicator type="error">{error}</StatusIndicator>
                <Button variant="link" onClick={retry}>
                  Retry
                </Button>
              </>
            ) : (
              <StatusIndicator type="loading">Connecting...</StatusIndicator>
            )}
          </div>
        )}

        {isConnected && isLoadingHistory && (
          <div className={styles.emptyState}>
            <StatusIndicator type="loading">
              Loading conversation...
            </StatusIndicator>
          </div>
        )}

        {isConnected && error && (
          <div className={styles.emptyState}>
            <StatusIndicator type="error">
              Something went wrong.
            </StatusIndicator>
            <Button variant="link" onClick={retry}>
              Reconnect
            </Button>
          </div>
        )}

        {inlineError && (
          <Alert
            type="error"
            dismissible
            onDismiss={dismissError}
            action={
              <Link
                href="https://t.corp.amazon.com/create/templates/082f27fb-9033-440a-861c-34c9d034f3a1"
                external
                variant="primary"
              >
                Report this issue
              </Link>
            }
          >
            {inlineError}
          </Alert>
        )}

        {isConnected &&
          items.length === 0 &&
          !isLoading &&
          !isLoadingHistory &&
          !error && (
            <div className={styles.emptyState}>
              <Icon name="gen-ai" size="big" />
              <Box variant="p" color="text-body-secondary">
                Ask me anything about your audits, tasks, notices, or
                litigations.
              </Box>
            </div>
          )}

        {items.map((item) => {
          if (item.type === "tool_call") {
            return (
              <div key={item.id} className={styles.toolCallRow}>
                <ToolCallIndicator
                  toolCall={{
                    toolUseId: item.toolUseId,
                    toolName: item.toolName,
                    status: item.status,
                    description: item.description,
                  }}
                />
              </div>
            );
          }

          return (
            <div
              key={item.id}
              className={`${styles.messageRow} ${
                item.role === "user" ? styles.messageRowUser : ""
              }`}
            >
              <div
                className={`${styles.avatar} ${
                  item.role === "user"
                    ? styles.avatarUser
                    : styles.avatarAssistant
                }`}
              >
                {item.role === "user" ? userInitials : <Icon name="gen-ai" />}
              </div>
              <div className={styles.messageBody}>
                <div
                  className={`${styles.messageContent} ${
                    item.role === "user" ? styles.messageContentUser : ""
                  }`}
                >
                  {item.role === "assistant" ? (
                    <MarkdownRenderer content={item.content} />
                  ) : (
                    item.displayContent || item.content
                  )}
                  {item.isStreaming && <span className={styles.cursor}>▎</span>}
                  {item.role === "assistant" &&
                    !item.isStreaming &&
                    item.content && (
                      <MessageCopyButton content={item.content} />
                    )}
                </div>
              </div>
            </div>
          );
        })}

        {isLoading &&
          !items.some(
            (item) =>
              item.type === "message" &&
              item.role === "assistant" &&
              item.isStreaming &&
              item.content !== "",
          ) && (
            <div className={styles.typingIndicator}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          )}

        {pendingConfirmation && (
          <div className={styles.confirmationBanner}>
            <Box variant="p">
              <strong>{pendingConfirmation.toolName}</strong>:{" "}
              {pendingConfirmation.description}
            </Box>
            <div className={styles.confirmationActions}>
              <Button
                variant="primary"
                onClick={() => handleConfirmation(true)}
              >
                Yes
              </Button>
              <Button
                variant="normal"
                onClick={() => handleConfirmation(false)}
              >
                No
              </Button>
            </div>
          </div>
        )}

        {items.some(
          (item) =>
            item.type === "message" &&
            item.role === "assistant" &&
            !item.isStreaming,
        ) && (
          <SessionFeedback
            selected={sessionFeedback}
            onSubmit={sendFeedback}
            sessionId={selectedSessionId}
            teamName={headerSelectedTeam ?? ""}
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      <form className={styles.inputArea} onSubmit={handleSubmit}>
        <div className={styles.inputRow}>
          <div className={styles.inputField}>
            <Textarea
              value={inputValue}
              onChange={({ detail }) => setInputValue(detail.value)}
              onKeyDown={(e) => {
                if (
                  e.detail.key === "Enter" &&
                  !e.detail.shiftKey &&
                  !isLoading
                ) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask Gaia..."
              disabled={!isConnected}
              ariaLabel="Message input"
              rows={2}
            />
          </div>
          {isLoading ? (
            <Button
              variant="inline-icon"
              iconName="stop-circle"
              ariaLabel="Stop response"
              onClick={interruptResponse}
            />
          ) : (
            <Button
              variant="primary"
              iconName="send"
              disabled={!inputValue.trim() || !isConnected}
              ariaLabel="Send message"
              onClick={handleSend}
            />
          )}
        </div>
      </form>
    </>
  );
};

export default AssistantChatContent;
